/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  ChatApiEndpointWithId,
  ChatApiEndpointFallbackStrategy,
  ProjectChatApiEndpointWithId,
  MessageRole,
} from '@scale/shared/types/chat';
import {
  Button,
  Typography,
  Modal,
  TextArea,
  Icon,
  Checkbox,
  Skeleton,
  Card,
  Tabs,
} from '@scale/scaleui';
import { Flex, Link, Tooltip, Text, SegmentedControl } from '@scale/scaleui-radix';
import {
  StrategyParamsSchemaMap,
  masterEndpointMap,
  CreateCompletionRequest,
  CreateCompletionResponse,
  getStrategyFromUrl,
} from '@scale/shared/types/chat-completion';

import { z } from 'zod';
import { ZodFormBuilder } from 'react-scale/corp/views/CorpProjectGroupManager/views/Linter/GenericLinterView/ZodFormBuilder';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import produce from 'immer';
import { useLocalStorage } from 'react-use-v13';
import * as _ from 'lodash';
import apiFetch from 'react-scale/lib/api-fetch';
import cn from 'classnames';
import { RadioCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/RadioCard';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { ModelErrorButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseSelector/ModelErrorButton';
import {
  ChatStoreContextProvider,
  useCreateChatStore,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import {
  useGetCustomerModelVersions,
  useGetModelConfiguration,
} from 'react-scale/corp/views/CorpProjectGroupManager/hooks/modelManagerApiClient';
import { useProject } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import {
  ModelEvalEndpointWithUserInfoDTO,
  ProjectModelConfigDTO,
  PopulatedProjectModelConfigDTO,
} from '@scale/customer-app-shared';
import {
  getDraftProjectModelConfigurationFromHistory,
  getActiveProjectModelConfiguration,
  getModelConfigurationsByIdAndVersion,
} from 'react-scale/lib/dashboard-api/selfserve';
import { convertDistributedParams } from 'react-scale/hooks/chat/useProjectModelConfigs';

const chatApiEndpointSchema = z.object({
  id: z.string(),
  url: z.string(),
  params: z.object({}),
  // This is represented by an enum ChatApiEndpointFallbackStrategy but exposed as a boolean in the TTE UI to make it clearer
  // whether its enabled or disabled
  fallbackStrategy: z.boolean().optional(),
});

// params are not included by default since we add them after an endpoint is selected
const fallbackApiEndpointSchema = z.object({
  id: z.string(),
  url: z.string(),
});

const DEFAULT_TEST_MESSAGE = 'This is a test message. What are you?';

enum EndpointTab {
  CUSTOMER = 'customer',
  CUSTOM = 'custom',
  PROJECT = 'project',
}

interface ChatApiEndpointsBuilderProps {
  name?: string;
  description?: string;
  existingValue?: Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>;
  updateProperty: (arg: Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>) => void;
  required?: boolean;
  maxNumberOfEndpoints?: number;
  customTestPrompt?: string;
  showTestButton?: boolean;
  includeProjectModelEndpointId?: boolean;
}

interface SingleEndpointCardProps {
  endpoint: ChatApiEndpointWithId;
  testMessageText?: string;
}

function isDiscriminatedUnion(
  schema: z.AnyZodObject | z.ZodDiscriminatedUnion<any, any>,
): schema is z.ZodDiscriminatedUnion<any, any> {
  return 'discriminator' in schema._def;
}

export function getDefaults<Schema extends z.AnyZodObject>(
  schema: Schema | z.ZodDiscriminatedUnion<any, any>,
) {
  if (isDiscriminatedUnion(schema)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(schema.shape).map(([key, value]) => {
      if (value instanceof z.ZodDefault) return [key, value._def.defaultValue()];
      if (value instanceof z.ZodString) return [key, ''];
      else if (value instanceof z.ZodNumber) return [key, 0];
      else if (value instanceof z.ZodBoolean) return [key, false];
      else if (value instanceof z.ZodArray) return [key, []];
      else if (value instanceof z.ZodObject) return [key, {}];
      else return [key, null];
    }),
  );
}

export const PROJECT_MODEL_CONFIG_VIEW_KEY = 'projectModelConfigView';

const ChatApiEndpointsBuilder: React.FC<ChatApiEndpointsBuilderProps> = ({
  name = 'Chat Models',
  description = `ALWAYS VERIFY THE URL + PARAMS WITH THE CUSTOMER. If you change something, remember to use 'Test Endpoints' and backfill!`,
  existingValue = [],
  updateProperty,
  maxNumberOfEndpoints,
  customTestPrompt,
  showTestButton = true,
  includeProjectModelEndpointId = false,
}) => {
  const projectId = useProjectId();
  const { data: projectData } = useProject({ projectId });

  const [activeProjectModelConfigurations, setActiveProjectModelConfigurations] = useState<
    Record<string, PopulatedProjectModelConfigDTO[]> | undefined
  >();
  const [draftProjectModelConfigurations, setDraftProjectModelConfigurations] = useState<
    Record<string, PopulatedProjectModelConfigDTO[]> | undefined
  >();
  const [hasDraftProjectModelConfiguration, setHasDraftProjectModelConfiguration] = useState(false);

  const hasProjectModelConfigurations = useMemo(
    () =>
      Object.keys(activeProjectModelConfigurations ?? {}).length > 0 ||
      hasDraftProjectModelConfiguration,
    [activeProjectModelConfigurations, hasDraftProjectModelConfiguration],
  );

  const [projectModelConfigView, setProjectModelConfigView] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(PROJECT_MODEL_CONFIG_VIEW_KEY) || 'active';
    }
    return 'active';
  });

  const handleProjectModelConfigViewChange = (val: string) => {
    // Only allow changing to enabled options
    if ((val === 'active' && hasActiveConfigs) || (val === 'proposed' && hasDraftConfigs)) {
      setProjectModelConfigView(val);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(PROJECT_MODEL_CONFIG_VIEW_KEY, val);
      }
    }
  };

  // Determine which configs are available
  const hasActiveConfigs = useMemo(
    () =>
      !!activeProjectModelConfigurations &&
      Object.keys(activeProjectModelConfigurations).length > 0,
    [activeProjectModelConfigurations],
  );
  const hasDraftConfigs = useMemo(
    () =>
      !!draftProjectModelConfigurations && Object.keys(draftProjectModelConfigurations).length > 0,
    [draftProjectModelConfigurations],
  );

  // Set initial value based on available configs
  useEffect(() => {
    if (!hasActiveConfigs && hasDraftConfigs && projectModelConfigView !== 'proposed') {
      setProjectModelConfigView('proposed');
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(PROJECT_MODEL_CONFIG_VIEW_KEY, 'proposed');
      }
    } else if (!hasDraftConfigs && hasActiveConfigs && projectModelConfigView !== 'active') {
      setProjectModelConfigView('active');
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(PROJECT_MODEL_CONFIG_VIEW_KEY, 'active');
      }
    }
  }, [hasActiveConfigs, hasDraftConfigs]);

  useEffect(() => {
    const populateProjectModelConfigurations = async () => {
      if (!projectId) {
        setHasDraftProjectModelConfiguration(false);
        return;
      }
      const draftProjectModelConfiguration = await getDraftProjectModelConfigurationFromHistory(
        projectId,
      );

      const activeProjectModelConfiguration = await getActiveProjectModelConfiguration(projectId);

      setHasDraftProjectModelConfiguration(
        !!draftProjectModelConfiguration?.projectConfigurationMapping,
      );
      const draftMapping = draftProjectModelConfiguration?.projectConfigurationMapping;
      const activeMapping = activeProjectModelConfiguration.projectModelConfiguration;

      // Helper to populate configs
      const populate = async (mapping: Record<string, ProjectModelConfigDTO[]> | undefined) => {
        if (!mapping) return undefined;
        const populated: Record<string, PopulatedProjectModelConfigDTO[]> = { ...mapping };
        for (const modelKey of Object.keys(populated)) {
          const modelConfigs = populated[modelKey];
          const detailedModelConfigs = await Promise.all(
            modelConfigs.map(async (modelConfig: ProjectModelConfigDTO) => {
              const detailedModelConfig = await getModelConfigurationsByIdAndVersion(
                modelConfig._id,
                modelConfig.version,
              );
              if (modelConfig.fallbackEndpoint) {
                const fallbackModelConfig = await getModelConfigurationsByIdAndVersion(
                  modelConfig.fallbackEndpoint._id,
                  modelConfig.fallbackEndpoint.version ?? 0,
                );
                return {
                  ...modelConfig,
                  ...detailedModelConfig,
                  fallbackEndpoint: {
                    ...fallbackModelConfig,
                    version: modelConfig.fallbackEndpoint.version ?? 0,
                  },
                };
              }
              return {
                ...modelConfig,
                ...detailedModelConfig,
              };
            }),
          );
          populated[modelKey] = detailedModelConfigs;
        }
        return populated;
      };

      const populatedActive = await populate(activeMapping);
      const populatedDraft = await populate(draftMapping);

      setActiveProjectModelConfigurations(populatedActive);
      setDraftProjectModelConfigurations(populatedDraft);
    };
    populateProjectModelConfigurations();
  }, [projectId]);

  // Choose which config to use based on the segmented control
  const projectModelConfigurations = useMemo(() => {
    return projectModelConfigView === 'proposed'
      ? draftProjectModelConfigurations
      : activeProjectModelConfigurations;
  }, [projectModelConfigView, draftProjectModelConfigurations, activeProjectModelConfigurations]);

  const { data: modelVersions, isLoading: modelVersionsLoading } = useGetCustomerModelVersions({
    customerId: projectData?.taskCustomer,
    projectId,
  });

  const endpoints = existingValue;

  const [searchTerm, setSearchTerm] = React.useState('');

  const [addEndpointModalOpen, setAddEndpointModalOpen] = React.useState(false);
  const [addProjectModelEndpointModalOpen, setAddProjectModelEndpointModalOpen] =
    React.useState(false);
  const [selectedEndpointTab, setSelectedEndpointTab] = React.useState<EndpointTab>(
    EndpointTab.CUSTOM,
  );

  const [idxForFallbackConfig, setIdxForFallbackConfig] = React.useState<number | null>();

  const enableFallbackEndpoint = useUnleashFeatureFlag(
    'fallback_model_endpoint',
    { projectId },
    true,
  );

  const adjustedChatApiEndpointSchema = enableFallbackEndpoint
    ? chatApiEndpointSchema
    : chatApiEndpointSchema.omit({ fallbackStrategy: true });

  const handleOpenAddEndpointModal = useCallback(() => {
    setSearchTerm('');
    setAddEndpointModalOpen(true);
  }, []);

  const handleAddEndpointModalClose = (selectedEndpoint: boolean) => {
    // Uncheck the fallbackStrategy box if no endpoint was selected in the modal
    if (!selectedEndpoint && !_.isNil(idxForFallbackConfig)) {
      updateProperty(
        produce(endpoints, draft => {
          (draft[idxForFallbackConfig] as ChatApiEndpointWithId).fallbackStrategy = undefined;
        }),
      );
    }

    setAddEndpointModalOpen(false);
    setIdxForFallbackConfig(null);
  };

  const handleAddCustomerEndpointModalClose = (modelConfig?: ModelEvalEndpointWithUserInfoDTO) => {
    if (modelConfig) {
      const newEndpoints = [...endpoints];
      newEndpoints.push({
        id: modelConfig.name,
        url: modelConfig.url ?? '',
        params: modelConfig.params ?? {},
        modelEndpointId: modelConfig._id,
      });
      updateProperty(newEndpoints);
      setAddEndpointModalOpen(false);
      setIdxForFallbackConfig(null);
    }
    setAddEndpointModalOpen(false);
  };

  const handleAddProjectEndpointModel = useCallback(
    (
      projectModelConfigurationKey: string,
      strategy: string,
      config: PopulatedProjectModelConfigDTO,
    ) => {
      const newEndpoints = [...endpoints];
      newEndpoints.push({
        // TODO: agree on a good naming convention for project endpoints
        id: `endpoint_${strategy}`,
        projectModelConfigurationKey,
        ...(includeProjectModelEndpointId && { modelEndpointId: config._id }), // Includes the current modelEndpointId for cases where we to use the current model specifically. For example, pass@k evals
      });
      updateProperty(newEndpoints);
      setAddProjectModelEndpointModalOpen(false);
      setIdxForFallbackConfig(null);
    },
    [updateProperty, endpoints],
  );

  const [testModalOpen, setTestModalOpen] = React.useState(false);

  const filteredEndpoints = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();

    return Object.entries(masterEndpointMap || {})
      .filter(([url, { strategy, description, customer }]) =>
        [strategy, url, description, customer].some(field =>
          field.toLowerCase().includes(lowerSearchTerm),
        ),
      )
      .map(([url, endpoint]) => ({ url, ...endpoint }))
      .sort((a, b) => a.strategy.localeCompare(b.strategy));
  }, [searchTerm]);

  const filteredCustomerEndpoints = useMemo(() => {
    if (!modelVersions) {
      return [];
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return (
      modelVersions?.filter(({ name, url, description, params }) => {
        return (
          name.toLowerCase().includes(lowerSearchTerm) ||
          url?.toLowerCase().includes(lowerSearchTerm) ||
          description?.toLowerCase().includes(lowerSearchTerm) ||
          JSON.stringify(params || {})
            .toLowerCase()
            .includes(lowerSearchTerm)
        );
      }) || []
    );
  }, [searchTerm, modelVersions]);

  // Add a ref to track if the user has manually changed the tab
  const hasUserChangedTab = useRef(false);

  // Determine if there is at least one project model endpoint added
  const hasProjectModelConfigsInList = useMemo(
    () =>
      endpoints.some(e => 'projectModelConfigurationKey' in e && !!e.projectModelConfigurationKey),
    [endpoints],
  );

  const endpointsToErrorMessages = useMemo(
    () =>
      endpoints.map(endpoint => {
        if (
          !endpoint.projectModelConfigurationKey &&
          !_.isNil((endpoint as ChatApiEndpointWithId).fallbackEndpoint) &&
          _.isEqual(
            _.pick(endpoint, ['url', 'params']),
            _.pick((endpoint as ChatApiEndpointWithId).fallbackEndpoint, ['url', 'params']),
          )
        ) {
          return '[FIX ME] Primary endpoint must be different from fallback';
        }
        return null;
      }),
    [updateProperty, endpoints],
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      const startIndex = result.source.index;
      const endIndex = result.destination.index;
      const newEndpoints = produce(endpoints, (draft: any[]) => {
        const [removed] = draft.splice(startIndex, 1);
        draft.splice(endIndex, 0, removed);
      });
      updateProperty(newEndpoints);
    },
    [updateProperty, endpoints],
  );

  const [jsonMode, setJsonMode] = useLocalStorage('chat-api-endpoints-json-mode', false);

  useEffect(() => {
    if (filteredCustomerEndpoints.length > 0 && !hasUserChangedTab.current) {
      setSelectedEndpointTab(EndpointTab.CUSTOMER);
    }
  }, [filteredCustomerEndpoints]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col">
        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-col self-start">
              {name && <Typography.Subtitle3>{name}</Typography.Subtitle3>}
              {description && (
                <Typography.Caption color={'text-primary-800'}>{description}</Typography.Caption>
              )}
            </div>
            <div className="flex flex-row items-center">
              <Button
                variant="transparent"
                icon={{
                  position: 'right',
                  type: ['fas', 'external-link-alt'],
                }}
                size="sm"
                onClick={() =>
                  window.open(
                    'https://scale.atlassian.net/wiki/spaces/EPD/pages/833880082/Model+endpoint+guide+FAQ',
                    '_blank',
                  )
                }
              >
                Documentation
              </Button>
              <Checkbox
                checked={jsonMode}
                onChange={() => setJsonMode(!jsonMode)}
                label={'View JSON'}
              />
            </div>
          </div>
        </div>
      </div>
      {hasProjectModelConfigsInList && hasProjectModelConfigurations && (
        <Flex direction="row" gap="2" align="center">
          <Text size="2" weight="medium">
            Project Model Configurations:
          </Text>
          <SegmentedControl.Root
            value={projectModelConfigView}
            size="2"
            onValueChange={handleProjectModelConfigViewChange}
          >
            <SegmentedControl.Item
              value="active"
              className={!hasActiveConfigs ? 'cursor-not-allowed' : ''}
            >
              Active
            </SegmentedControl.Item>
            <SegmentedControl.Item
              value="proposed"
              className={!hasDraftConfigs ? 'cursor-not-allowed' : ''}
            >
              Proposed
            </SegmentedControl.Item>
          </SegmentedControl.Root>
        </Flex>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppable-endpoints">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="pt-2">
              {endpoints.map((endpoint, index) => (
                <Draggable key={index} draggableId={`draggable-${endpoint.id}`} index={index}>
                  {provided => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <div className="flex items-center py-1">
                        <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                          <Icon variant="neutral" size="md" icon="grip-vertical" />
                        </div>
                        <div className="flex-grow">
                          {!endpoint.projectModelConfigurationKey ? (
                            <ZodFormBuilder
                              disabled={!!(endpoint as ChatApiEndpointWithId).modelEndpointId}
                              label={
                                endpointsToErrorMessages[index] != null
                                  ? endpointsToErrorMessages[index]!
                                  : endpoint.id
                              }
                              key={index}
                              schema={adjustedChatApiEndpointSchema.extend(
                                (() => {
                                  const chatEndpoint = endpoint as ChatApiEndpointWithId;
                                  if (jsonMode) {
                                    return {
                                      params: z.any(),
                                      ...(chatEndpoint.fallbackStrategy && {
                                        fallbackEndpoint: fallbackApiEndpointSchema.extend({
                                          params: z.any(),
                                        }),
                                      }),
                                    };
                                  }
                                  try {
                                    if (endpoint.projectModelConfigurationKey || !chatEndpoint.url)
                                      return {};
                                    const strategyParamsSchema =
                                      StrategyParamsSchemaMap[
                                        masterEndpointMap[chatEndpoint.url].strategy
                                      ];
                                    const fallbackParamsSchema = chatEndpoint.fallbackEndpoint?.url
                                      ? StrategyParamsSchemaMap[
                                          masterEndpointMap[chatEndpoint.fallbackEndpoint?.url]
                                            .strategy
                                        ]
                                      : null;
                                    return {
                                      params: strategyParamsSchema || z.object({}),
                                      ...(chatEndpoint.fallbackStrategy && {
                                        fallbackEndpoint: fallbackApiEndpointSchema.extend({
                                          params: fallbackParamsSchema ?? z.object({}),
                                        }),
                                      }),
                                    };
                                  } catch (e) {
                                    return {
                                      params: z.any(),
                                      ...(chatEndpoint.fallbackStrategy && {
                                        fallbackEndpoint: fallbackApiEndpointSchema.extend({
                                          params: z.any(),
                                        }),
                                      }),
                                    };
                                  }
                                })(),
                              )}
                              value={endpoint}
                              onChange={(newEndpoint: ChatApiEndpointWithId) => {
                                const chatEndpoint = newEndpoint as ChatApiEndpointWithId;
                                // Open the "Add endpoint" modal when a fallback strategy is selected
                                if (
                                  chatEndpoint.fallbackStrategy &&
                                  !chatEndpoint.fallbackStrategy
                                ) {
                                  setAddEndpointModalOpen(true);
                                  setIdxForFallbackConfig(index);
                                }
                                updateProperty(
                                  produce(endpoints, draft => {
                                    draft[index] = newEndpoint;
                                    // Reset if box is unchecked
                                    (draft[index] as ChatApiEndpointWithId).fallbackEndpoint =
                                      chatEndpoint.fallbackStrategy
                                        ? (draft[index] as ChatApiEndpointWithId).fallbackEndpoint
                                        : undefined;
                                    (draft[index] as ChatApiEndpointWithId).fallbackStrategy =
                                      chatEndpoint.fallbackStrategy
                                        ? ChatApiEndpointFallbackStrategy.FALLBACK_ON_ERROR
                                        : undefined;
                                  }),
                                );
                              }}
                              isChatEndpointBuilder
                              parseErrorMessage={(() => {
                                const chatEndpoint = endpoint as ChatApiEndpointWithId;
                                const parsedParams = _.omitBy(chatEndpoint.params, _.isNull);
                                if (!chatEndpoint.url)
                                  return {
                                    id: chatEndpoint.id,
                                    message: 'Endpoint URL is required',
                                  };

                                if (!masterEndpointMap[chatEndpoint.url]) {
                                  return {
                                    id: chatEndpoint.id,
                                    message: 'Endpoint not found in masterEndpointMap',
                                  };
                                }
                                const tryParse =
                                  StrategyParamsSchemaMap[
                                    masterEndpointMap[chatEndpoint.url].strategy
                                  ].safeParse(parsedParams);
                                if (!tryParse.success) {
                                  const e = tryParse.error;
                                  return {
                                    id: chatEndpoint.id,
                                    message: `${e.issues[0].path}: ${e.issues[0].message}`,
                                  };
                                }

                                // Custom temperature validation for o1-preview and o1-mini
                                if (
                                  (parsedParams.model === 'o1-preview' ||
                                    parsedParams.model === 'o1-mini') &&
                                  parsedParams.temperature !== 1
                                ) {
                                  return {
                                    id: chatEndpoint.id,
                                    message:
                                      'Temperature must be 1 when model is o1-preview or o1-mini',
                                  };
                                }
                              })()}
                            />
                          ) : (
                            <div className="rounded-lg shadow bg-neutral-0 p-4">
                              {endpoint.projectModelConfigurationKey} Current{' '}
                              {projectModelConfigView === 'proposed' ? 'proposed' : 'mapped'}{' '}
                              models:{' '}
                              {projectModelConfigurations?.[
                                (endpoint as ProjectChatApiEndpointWithId)
                                  .projectModelConfigurationKey
                              ].map((config: PopulatedProjectModelConfigDTO, mIndex: number) => {
                                return (
                                  <>
                                    <Tooltip
                                      content={
                                        <Flex direction="column" gap="2">
                                          <Text size="2" weight="medium">
                                            {config.name}
                                          </Text>
                                          <div>URL: {config.url}</div>
                                          <div>Distribution: {config.distribution}%</div>
                                          <div>
                                            <pre>{JSON.stringify(config.params, null, 2)}</pre>
                                          </div>
                                        </Flex>
                                      }
                                    >
                                      <Link
                                        key={config?._id}
                                        target="_blank"
                                        href={`https://console.scale.com/projects/${projectId}?spoof=${projectData?.taskCustomer}&view=models&showCorpFeatures=true`}
                                      >
                                        <ProjectModelVersionName modelId={config?._id} />
                                      </Link>
                                    </Tooltip>
                                    {mIndex !==
                                      projectModelConfigurations[
                                        (endpoint as ProjectChatApiEndpointWithId)
                                          .projectModelConfigurationKey
                                      ].length -
                                        1 && <span>, </span>}
                                  </>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <Icon
                          icon="trash"
                          tooltip="remove item"
                          onClick={() =>
                            updateProperty(
                              produce(endpoints, draft => {
                                draft.splice(index, 1);
                              }),
                            )
                          }
                        />
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="flex flex-row gap-4 pt-2">
        {(maxNumberOfEndpoints === undefined || endpoints.length < maxNumberOfEndpoints) &&
          hasProjectModelConfigurations && (
            <>
              <Tooltip
                content={
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="medium">
                      Project Model Endpoints have been previously configured.
                    </Text>
                    <Link
                      underline="always"
                      color="accent"
                      target="_blank"
                      href={`https://console.scale.com/projects/${projectId}?spoof=${projectData?.taskCustomer}&view=models&showCorpFeatures=true`}
                    >
                      Click here to update them.
                    </Link>
                  </Flex>
                }
              >
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setAddProjectModelEndpointModalOpen(true)}
                  icon={{
                    position: 'left',
                    type: ['fas', 'plus'],
                  }}
                >
                  Add Project Model Endpoint
                </Button>
              </Tooltip>
            </>
          )}
        {(maxNumberOfEndpoints === undefined || endpoints.length < maxNumberOfEndpoints) && (
          <>
            <Tooltip
              content={
                hasProjectModelConfigurations
                  ? 'Project Model Endpoints have been previously configured, only add a custom endpoint if you need to override them.'
                  : 'Prefer to add a customer defined model version if available'
              }
            >
              <Button
                size="sm"
                variant={hasProjectModelConfigurations ? 'neutral' : 'secondary'}
                onClick={() => handleOpenAddEndpointModal()}
                icon={{
                  position: 'left',
                  type: ['fas', 'plus'],
                }}
              >
                Add Custom Endpoint
              </Button>
            </Tooltip>
          </>
        )}
        {showTestButton && (
          <Button
            size="sm"
            variant="primary"
            onClick={async () => {
              setTestModalOpen(true);
            }}
            icon={{
              position: 'left',
              type: ['fas', 'arrow-right'],
            }}
          >
            Test Endpoints
          </Button>
        )}
      </div>
      <Modal
        open={addEndpointModalOpen}
        onClose={() => {
          handleAddEndpointModalClose(false);
        }}
        title="Supported Endpoints"
        innerPanelClassName="pt-0"
      >
        <div className="flex flex-col" style={{ width: '600px' }}>
          <div className="sticky top-0 py-2 bg-neutral-0 flex flex-col gap-2 z-10">
            <Typography.Paragraph2>
              Need to add a new endpoint? Reach out to #genai-chat-task-eng
            </Typography.Paragraph2>
            <TextArea
              placeholder="Search for an endpoint"
              onChange={e => setSearchTerm(e.target.value)}
              rows={1}
              resizable={false}
            />

            <Tabs
              className="mb-2"
              onChange={e => {
                setSelectedEndpointTab(e as EndpointTab);
                hasUserChangedTab.current = true;
              }}
              tabs={[
                ...(filteredCustomerEndpoints.length > 0
                  ? [
                      {
                        label: 'Existing Customer Models',
                        id: EndpointTab.CUSTOMER,
                      },
                    ]
                  : []),
                {
                  label: 'Custom Endpoints',
                  id: EndpointTab.CUSTOM,
                },
              ]}
              value={selectedEndpointTab}
              variant="default"
            />
          </div>
          {selectedEndpointTab === EndpointTab.CUSTOMER && (
            <>
              {modelVersionsLoading ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-8" />
                  <Skeleton className="h-8" />
                  <Skeleton className="h-8" />
                  <Skeleton className="h-8" />
                </div>
              ) : filteredCustomerEndpoints.length === 0 ? (
                <div className="flex flex-col gap-2 w-full">
                  <Typography.Paragraph2>No customer model versions found</Typography.Paragraph2>
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  {filteredCustomerEndpoints.map(modelVersion => (
                    <div
                      key={modelVersion._id}
                      className="flex w-full flex-grow rounded-lg p-2 cursor-pointer hover:bg-neutral-100"
                    >
                      <Card
                        title={modelVersion.name}
                        description={modelVersion.url}
                        className="w-full"
                      >
                        <div className="flex flex-col gap-3 w-full">
                          <Typography.Subtitle3 className="text-wrap">
                            {modelVersion.description}
                          </Typography.Subtitle3>

                          <div>
                            <Typography.Paragraph2 className="mb-2">
                              Endpoint Parameters:
                            </Typography.Paragraph2>
                            <ReactJsonView
                              style={{
                                zIndex: 1,
                              }}
                              src={modelVersion.params ?? {}}
                              enableClipboard={true}
                              collapsed={true}
                              displayDataTypes={false}
                              displayObjectSize={false}
                            />
                          </div>

                          <Button
                            className="w-fit"
                            size="sm"
                            variant="secondary"
                            icon={{
                              position: 'left',
                              type: ['fas', 'plus'],
                            }}
                            onClick={() => handleAddCustomerEndpointModalClose(modelVersion)}
                          >
                            Add Model Version
                          </Button>
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {selectedEndpointTab === EndpointTab.CUSTOM && (
            <>
              {filteredEndpoints.map(({ url, strategy, description, customer }) => {
                const paramsSchema = StrategyParamsSchemaMap[strategy];
                // paramsSchema is a zod schema; let's get the default params:
                const defaultParams = getDefaults(paramsSchema);

                const id = `endpoint_${strategy}_${
                  idxForFallbackConfig != null
                    ? `${idxForFallbackConfig + 1}_fallback`
                    : endpoints.length + 1
                }`;

                return (
                  <div
                    className="flex w-full flex-grow rounded-lg p-2 cursor-pointer hover:bg-neutral-100"
                    key={url}
                  >
                    <Card
                      className="w-full"
                      title={strategy}
                      description={url}
                      tag={{ label: customer, icon: 'tag', variant: 'secondary' }}
                      onClick={() => {
                        if (idxForFallbackConfig != null) {
                          updateProperty(
                            produce(endpoints, draft => {
                              (
                                draft[idxForFallbackConfig] as ChatApiEndpointWithId
                              ).fallbackEndpoint = {
                                id,
                                url,
                                params: defaultParams,
                              };
                            }),
                          );
                        } else {
                          const newEndpoints = [...endpoints];
                          newEndpoints.push({
                            id,
                            url,
                            params: defaultParams,
                          });
                          updateProperty(newEndpoints);
                        }
                        handleAddEndpointModalClose(true);
                      }}
                    >
                      <Typography.Subtitle3 className="text-wrap">
                        {description}
                      </Typography.Subtitle3>
                    </Card>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </Modal>
      <Modal
        open={addProjectModelEndpointModalOpen}
        onClose={() => {
          setAddProjectModelEndpointModalOpen(false);
        }}
        title="Supported Endpoints"
        innerPanelClassName="pt-0"
      >
        <div className="flex flex-col" style={{ width: '600px' }}>
          <div className="sticky top-0 py-2 bg-neutral-0 flex flex-col gap-2 z-10">
            <Typography.Paragraph2>
              Need to add a new endpoint? Reach out to #genai-chat-task-eng
            </Typography.Paragraph2>
            <TextArea
              placeholder="Search for an endpoint"
              onChange={e => setSearchTerm(e.target.value)}
              rows={1}
              resizable={false}
            />
          </div>

          <div className="flex flex-col gap-2 mt-2 w-full">
            <Typography.Subtitle3>
              The following models are currently mapped to this project.{' '}
              <Link
                className="cursor-pointer"
                underline="always"
                color="accent"
                target="_blank"
                href={`https://console.scale.com/projects/${projectId}?spoof=${projectData?.taskCustomer}&view=models&showCorpFeatures=true`}
              >
                Click here to update model mappings
              </Link>
            </Typography.Subtitle3>
            {projectModelConfigurations &&
              Object.entries(projectModelConfigurations).map(([modelKey, configs]) => {
                return (
                  <div className="flex w-full" key={modelKey}>
                    <Card title={modelKey} className="flex-1 w-full">
                      <div className="flex flex-col gap-2 flex-1 w-full">
                        <div className="flex flex-row justify-between w-full">
                          <Typography.Subtitle2>Currently Mapped Models</Typography.Subtitle2>
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={{
                              position: 'left',
                              type: ['fas', 'plus'],
                            }}
                            onClick={() => {
                              const strategy = configs[0].url
                                ? getStrategyFromUrl(configs[0].url)
                                : undefined;
                              handleAddProjectEndpointModel(
                                modelKey,
                                strategy ?? 'Strategy',
                                configs[0],
                              );
                            }}
                          >
                            Use project model
                          </Button>
                        </div>
                        {configs.map((config: PopulatedProjectModelConfigDTO, index: number) => {
                          const { name, url, params, distribution, fallbackEndpoint } = config;

                          return (
                            <>
                              <div className="flex flex-col gap-2">
                                <Typography.Subtitle3>{name}</Typography.Subtitle3>
                                <Typography.Paragraph2>{url}</Typography.Paragraph2>
                                <Typography.Paragraph2>
                                  Distribution: {distribution} %
                                </Typography.Paragraph2>
                                <ReactJsonView
                                  name={'params'}
                                  src={params ?? {}}
                                  enableClipboard={true}
                                  collapsed={false}
                                  displayDataTypes={false}
                                  displayObjectSize={false}
                                />
                                {fallbackEndpoint && (
                                  <div className="flex flex-col gap-2 ml-4">
                                    <Typography.Subtitle3>Fallback Endpoint</Typography.Subtitle3>
                                    <Typography.Subtitle3>
                                      {fallbackEndpoint._id}
                                    </Typography.Subtitle3>
                                    <Typography.Paragraph2>
                                      {fallbackEndpoint.url}
                                    </Typography.Paragraph2>
                                    <ReactJsonView
                                      name={'params'}
                                      src={fallbackEndpoint.params ?? {}}
                                      enableClipboard={true}
                                      collapsed={false}
                                    />
                                  </div>
                                )}
                                {index !== configs.length - 1 && <hr className="my-2" />}
                              </div>
                            </>
                          );
                        })}
                      </div>
                    </Card>
                  </div>
                );
              })}
          </div>
        </div>
      </Modal>
      {showTestButton && (
        <Modal open={testModalOpen} onClose={() => setTestModalOpen(false)} title="Test Endpoints">
          <div className="flex flex-col gap-2" style={{ width: '600px' }}>
            <Typography.Paragraph2>
              Prompt: "{customTestPrompt ?? DEFAULT_TEST_MESSAGE}"
            </Typography.Paragraph2>
            <RadioCard.Group className="space-y-4">
              {endpoints.flatMap(endpoint => {
                // If endpoint is a project model config endpoint, use the populated config for the card
                if (endpoint.projectModelConfigurationKey && projectModelConfigurations) {
                  const configs = projectModelConfigurations[endpoint.projectModelConfigurationKey];
                  if (configs && configs.length > 0) {
                    // Render a SingleEndpointCard for each config and its fallback (if any)
                    return configs.flatMap((config, i) => {
                      const cards = [
                        <SingleEndpointCard
                          key={`${endpoint.id}-${i}`}
                          endpoint={{ ...config, id: `${endpoint.id}-${i}` } as any}
                          testMessageText={customTestPrompt}
                        />,
                      ];
                      if (config.fallbackEndpoint) {
                        cards.push(
                          <SingleEndpointCard
                            key={`${endpoint.id}-${i}-fallback`}
                            endpoint={
                              {
                                ...config.fallbackEndpoint,
                                id: `${endpoint.id}-${i}-fallback`,
                              } as any
                            }
                            testMessageText={customTestPrompt}
                          />,
                        );
                      }
                      return cards;
                    });
                  }
                }
                // Otherwise, use the endpoint as is
                return [
                  <SingleEndpointCard
                    key={endpoint.id}
                    endpoint={endpoint as ChatApiEndpointWithId}
                    testMessageText={customTestPrompt}
                  />,
                  ...((endpoint as ChatApiEndpointWithId).fallbackEndpoint
                    ? [
                        <SingleEndpointCard
                          key={`${endpoint.id}-fallback`}
                          endpoint={(endpoint as ChatApiEndpointWithId).fallbackEndpoint!}
                          testMessageText={customTestPrompt}
                        />,
                      ]
                    : []),
                ];
              })}
            </RadioCard.Group>
          </div>
        </Modal>
      )}
    </div>
  );
};

const SingleEndpointCardComponent: React.FC<SingleEndpointCardProps> = ({
  endpoint,
  testMessageText,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState({ response: '' });
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (endpoint: ChatApiEndpointWithId) => {
    if (endpoint.params?.sampleModelParams) {
      setIsError(true);
      setLastResponse({
        response: 'sampleModelParams is not supported yet. Please use the Chat Task Preview.',
      });
      return;
    }
    setIsLoading(true);
    setIsError(false);
    const testMessage = [
      {
        role: MessageRole.User,
        content: testMessageText ?? DEFAULT_TEST_MESSAGE,
      },
    ];
    const endpointDetails = masterEndpointMap[endpoint.url!];
    if (!endpointDetails) {
      setLastResponse({ response: `Endpoint not found with url: ${endpoint.url}` });
      setIsLoading(false);
      setIsError(true);
      return;
    }
    const strategyName = endpointDetails.strategy;
    const parsedParams = convertDistributedParams(_.omitBy(endpoint.params, _.isNull));
    try {
      StrategyParamsSchemaMap[strategyName].parse(parsedParams);
    } catch (e) {
      setLastResponse({ response: `${e.issues[0].path}: ${e.issues[0].message}` });
      setIsLoading(false);
      setIsError(true);
      return;
    }
    // Always turn off streaming for testing
    if (parsedParams.stream) {
      parsedParams.stream = false;
    }
    const body: CreateCompletionRequest<typeof strategyName> = {
      strategyName,
      params: parsedParams,
      messages: testMessage,
      projectId: null,
      useCase: 'chat-main',
    };

    try {
      const response = await apiFetch<CreateCompletionResponse>(
        null,
        'internal/self_serve/rlhf/createCompletion',
        {
          method: 'POST',
          body,
        },
      );
      setLastResponse({ response: response.choices[0].content });
    } catch (e) {
      setIsError(true);
      setLastResponse({ response: e.response.message });
    }
    setIsLoading(false);
  };

  useMemo(() => {
    handleSubmit(endpoint);
  }, [endpoint]);

  const chatStore = useCreateChatStore({
    config: {
      before: [],
      turn: [],
      after: [],
    },
  });

  return (
    <RadioCard
      value={endpoint.id}
      className={cn('h-full shadow-none w-96')}
      subtitle={
        <div className="flex justify-between items-center gap-1">
          <div className="font-medium text-neutral-800">{endpoint.id}</div>
        </div>
      }
      disabled={true}
    >
      {isLoading && <Skeleton className={'w-full'} />}
      {!isLoading && isError && (
        <ModelErrorButton
          errorMessage={`Error: ${lastResponse.response}`}
          onRetry={async () => {
            handleSubmit(endpoint);
          }}
          retryCooldown={0}
        />
      )}
      {!isLoading && !isError && (
        <ChatStoreContextProvider store={chatStore}>
          <ChatMarkdown className="text-sm" markdown={lastResponse.response} loading={isLoading} />
        </ChatStoreContextProvider>
      )}
    </RadioCard>
  );
};

export const SingleEndpointCard = React.memo(
  SingleEndpointCardComponent,
  (prevProps, nextProps) =>
    prevProps.endpoint.id === nextProps.endpoint.id &&
    prevProps.endpoint.url === nextProps.endpoint.url &&
    _.isEqual(prevProps.endpoint.params, nextProps.endpoint.params) &&
    prevProps.testMessageText === nextProps.testMessageText,
);

const ProjectModelVersionName = ({ modelId }: { modelId: string }) => {
  const { data: model, isLoading: isLoadingModel } = useGetModelConfiguration(modelId);
  if (isLoadingModel) {
    return <Skeleton className="w-16" />;
  }
  if (model?.params?.model) {
    return model.params.model;
  } else if (model?.name) {
    return model.name;
  }
  return modelId;
};

export default ChatApiEndpointsBuilder;
